import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { VideoLessonDialogProps } from '../types';

export function VideoLessonDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
}: VideoLessonDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-3xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Sort ID *</Label>
              <Input
                type="number"
                value={formData.sort_id}
                onChange={(e) =>
                  onFormDataChange((prev) => ({
                    ...prev,
                    sort_id: Number.isNaN(Number(e.target.value)) ? 1 : Number(e.target.value),
                  }))
                }
                className={cn(
                  isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: 'practical' | 'theoritical' | 'other') =>
                  onFormDataChange((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger
                  className={cn(
                    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="practical">Practical</SelectItem>
                  <SelectItem value="theoritical">Theoritical</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Video lesson title"
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>URL *</Label>
            <Input
              value={formData.url}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Description</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
              rows={4}
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(
              isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200'
            )}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
